import React, { useMemo } from 'react';
import { Holding, Currency, Loan } from '../types';
import { convertCurrency, formatCurrency } from '../utils/currency';
import { Card, CardContent } from './ui/Card';
import { Wallet, TrendingUp, PiggyBank, Building2, Landmark, ShieldCheck } from 'lucide-react';
interface KPISectionProps {
  holdings: Holding[];
  baseCurrency: Currency;
  partnerNames: [string, string];
  loans?: Loan[];
}
export function KPISection({
  holdings,
  baseCurrency,
  partnerNames,
  loans = []
}: KPISectionProps) {
  const totalAssets = holdings.reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  const totalLiquid = holdings.filter(h => h.type === 'Liquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  const totalIlliquid = holdings.filter(h => h.type === 'Illiquid').reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  // Real estate breakdown
  const reHoldings = holdings.filter(h => h.isRealEstate);
  const hasRealEstate = reHoldings.length > 0;
  const reMetrics = useMemo(() => {
    if (!hasRealEstate) return null;
    let grossValue = 0;
    let totalDebt = 0;
    for (const h of reHoldings) {
      const propertyValue = convertCurrency(h.amount, h.currency, baseCurrency);
      grossValue += propertyValue;
      if (h.linkedLoanId) {
        const loan = loans.find(l => l.id === h.linkedLoanId);
        if (loan && loan.isActive) {
          const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
          totalDebt += convertCurrency(principal, loan.currency, baseCurrency);
        }
      }
    }
    const netEquity = grossValue - totalDebt;
    const ltv = grossValue > 0 ? totalDebt / grossValue * 100 : 0;
    return {
      grossValue,
      totalDebt,
      netEquity,
      ltv
    };
  }, [reHoldings, loans, baseCurrency]);
  // Total debt across ALL active loans (not just RE-linked)
  const totalAllDebt = useMemo(() => {
    return loans.filter(l => l.isActive).reduce((sum, l) => {
      const principal = Math.max(0, l.totalAmount - l.depositAmount);
      return sum + convertCurrency(principal, l.currency, baseCurrency);
    }, 0);
  }, [loans, baseCurrency]);
  // Net worth = total assets minus total debt
  const totalNetWorth = totalAssets - totalAllDebt;
  // Non-RE debt (car loans, student debt, etc.)
  const nonReDebt = useMemo(() => {
    const reLinkedLoanIds = new Set(holdings.filter(h => h.isRealEstate && h.linkedLoanId).map(h => h.linkedLoanId!));
    return loans.filter(l => l.isActive && !reLinkedLoanIds.has(l.id)).reduce((sum, l) => {
      const principal = Math.max(0, l.totalAmount - l.depositAmount);
      return sum + convertCurrency(principal, l.currency, baseCurrency);
    }, 0);
  }, [loans, holdings, baseCurrency]);
  const rawTotals = holdings.reduce((acc, h) => {
    acc[h.currency] = (acc[h.currency] || 0) + h.amount;
    return acc;
  }, {} as Record<string, number>);
  return <div className="space-y-6" data-id="element-653">
      {/* Primary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-654">
        <KpiCard title="Total Net Worth" amount={totalNetWorth} currency={baseCurrency} icon={<Wallet className="w-5 h-5 text-blue-600" data-id="element-656" />} highlight data-id="element-655" />
        <KpiCard title="Total Liquid" amount={totalLiquid} currency={baseCurrency} icon={<PiggyBank className="w-5 h-5 text-blue-500" data-id="element-658" />} data-id="element-657" />
        <KpiCard title="Total Illiquid" amount={totalIlliquid} currency={baseCurrency} icon={<TrendingUp className="w-5 h-5 text-slate-500" data-id="element-660" />} data-id="element-659" />
      </div>

      {/* Total Debt KPI — shown when any loans exist */}
      {totalAllDebt > 0 && !hasRealEstate && <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-661">
          <KpiCard title="Total Outstanding Debt" amount={totalAllDebt} currency={baseCurrency} icon={<Landmark className="w-5 h-5 text-rose-500" data-id="element-663" />} accentColor="rose" negative data-id="element-662" />
        </div>}

      {/* Real Estate Equity vs Debt KPIs — only shown when RE assets exist */}
      {hasRealEstate && reMetrics && <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-664">
          <KpiCard title="Property Value" amount={reMetrics.grossValue} currency={baseCurrency} icon={<Building2 className="w-5 h-5 text-indigo-600" data-id="element-666" />} accentColor="indigo" data-id="element-665" />
          <KpiCard title="Mortgage Debt" amount={reMetrics.totalDebt} currency={baseCurrency} icon={<Landmark className="w-5 h-5 text-rose-500" data-id="element-668" />} accentColor="rose" negative data-id="element-667" />
          <KpiCard title="Net Equity" amount={reMetrics.netEquity} currency={baseCurrency} icon={<ShieldCheck className="w-5 h-5 text-emerald-600" data-id="element-670" />} accentColor="emerald" data-id="element-669" />
          <Card data-id="element-671">
            <CardContent className="p-5" data-id="element-672">
              <div className="flex justify-between items-start mb-4" data-id="element-673">
                <div className="bg-slate-50 p-2 rounded-lg" data-id="element-674">
                  <TrendingUp className="w-5 h-5 text-slate-500" data-id="element-675" />
                </div>
              </div>
              <div data-id="element-676">
                <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-677">
                  Loan-to-Value
                </p>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-678">
                  {reMetrics.ltv.toFixed(1)}%
                </h3>
                <div className="mt-2 h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-679">
                  <div className={`h-full rounded-full transition-all ${reMetrics.ltv > 80 ? 'bg-rose-500' : reMetrics.ltv > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{
                width: `${Math.min(reMetrics.ltv, 100)}%`
              }} data-id="element-680" />
                </div>
                <p className="text-xs text-slate-400 mt-1.5" data-id="element-681">
                  {reMetrics.ltv > 80 ? 'High leverage' : reMetrics.ltv > 60 ? 'Moderate leverage' : 'Conservative'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>}

      {/* Non-RE Debt — shown when RE exists AND there are also non-RE loans (car, student, etc.) */}
      {hasRealEstate && nonReDebt > 0 && <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-682">
          <KpiCard title="Other Debt (Non-Property)" amount={nonReDebt} currency={baseCurrency} icon={<Landmark className="w-5 h-5 text-rose-400" data-id="element-684" />} accentColor="rose" negative data-id="element-683" />
        </div>}

      {/* Currency breakdown */}
      {Object.keys(rawTotals).length > 0 && <div className={`grid gap-4 ${Object.keys(rawTotals).length === 1 ? 'grid-cols-1' : Object.keys(rawTotals).length === 2 ? 'grid-cols-1 sm:grid-cols-2' : Object.keys(rawTotals).length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`} data-id="element-685">
          {Object.entries(rawTotals).map(([currency, amount]) => {
        const flag = currency === 'USD' ? '🇺🇸' : currency === 'EUR' ? '🇪🇺' : currency === 'GBP' ? '🇬🇧' : currency === 'SGD' ? '🇸🇬' : '💱';
        return <Card key={currency} data-id="element-686">
                <CardContent className="p-4" data-id="element-687">
                  <div className="flex items-center gap-2 mb-2" data-id="element-688">
                    <span className="text-lg" data-id="element-689">{flag}</span>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-690">
                      {currency}
                    </span>
                  </div>
                  <p className="text-xl font-bold text-slate-900" data-id="element-691">
                    {formatCurrency(amount, currency as Currency)}
                  </p>
                  {baseCurrency !== currency && <p className="text-xs text-slate-400 mt-1" data-id="element-692">
                      ≈{' '}
                      {formatCurrency(convertCurrency(amount, currency as Currency, baseCurrency), baseCurrency)}
                    </p>}
                </CardContent>
              </Card>;
      })}
        </div>}
    </div>;
}
function KpiCard({
  title,
  amount,
  currency,
  icon,
  trend,
  subtitle,
  subtitleSecondary,
  highlight = false,
  accentColor,
  negative = false
}: {
  title: string;
  amount: number;
  currency: Currency;
  icon: React.ReactNode;
  trend?: string;
  subtitle?: string;
  subtitleSecondary?: string;
  highlight?: boolean;
  accentColor?: 'indigo' | 'rose' | 'emerald';
  negative?: boolean;
}) {
  const bgMap = {
    indigo: 'bg-indigo-50',
    rose: 'bg-rose-50',
    emerald: 'bg-emerald-50'
  };
  const iconBg = accentColor ? bgMap[accentColor] : highlight ? 'bg-blue-50' : 'bg-slate-50';
  return <Card className={highlight ? 'ring-2 ring-blue-500 shadow-md' : ''} data-id="element-693">
      <CardContent className="p-5" data-id="element-694">
        <div className="flex justify-between items-start mb-4" data-id="element-695">
          <div className={`p-2 rounded-lg ${iconBg}`} data-id="element-696">{icon}</div>
          {trend && <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-697">
              {trend}
            </span>}
        </div>
        <div data-id="element-698">
          <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-699">{title}</p>
          {subtitle ? <div className="space-y-1" data-id="element-700">
              <p className="text-sm font-semibold text-slate-800" data-id="element-701">{subtitle}</p>
              {subtitleSecondary && <p className="text-sm font-semibold text-slate-800" data-id="element-702">
                  {subtitleSecondary}
                </p>}
            </div> : <h3 className={`text-2xl font-bold ${negative ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-703">
              {formatCurrency(amount, currency)}
            </h3>}
        </div>
      </CardContent>
    </Card>;
}